// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.TreeMap.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import * as d3 from 'd3';
import 'd3-selection-multi';
import {
  MARGIN,
  TRANSITION_DURATION,
  HOVER_OPACITY,
  LAYOUT_CLASSES,
  TREEMAP_BANNER_HEIGHT
} from 'logic/data/Izenda.D3VizEngineConstants';
import { first, filter, map, get, isEmpty } from 'lodash/fp';
import { addClipPath, beginPlotAnimation, onDrilldown, setLabelsClick, isLeafNode } from '../Izenda.D3DrawUtils';
import { addTooltipContent } from '../commonDrawing/Izenda.D3TooltipDrawing';
import { drawLegend } from '../commonDrawing/Izenda.D3LegendDrawing';
import { drawBackground } from '../commonDrawing/Izenda.D3BackgroundDrawing';
import { drawTreemapLabels } from './Izenda.D3TreemapLabels';

export const chartStyle = 'd3treemap';

// Add margin for each 200 pixel in plot width
const ADDITIONAL_PLOT_MARGIN_DIVISION = 200;
// Padding of tree map
const TREEMAP_PADDING = 1;

/**
 * Draw the treemap
 * @param {d3.Selection<SVGElement>} svg The svg selection of the chart
 * @param {*} options The chart options
 */
export function draw(svg: d3.Selection<SVGElement>, options: Object) {
  const {
    container,
    series,
    plotOptions: {
      series: {
        cursor,
        dataLabels: { rotation }
      }
    },
    tooltip: { formatter: tooltipFormatter },
    izMultiLevel,
    commonActions: commonActions,
    izendaSeriesConfig,
    izendaOptions: { showSliceLabel: izSliceLabel, showValueLabel: izValueLabel }
  } = options;

  const { clientWidth: width, clientHeight: height } = container;

  const serie = first(series);
  const { data: root, display = true } = serie;

  // Get the greatest height of the tree
  const treeHeight = root.height;

  // Get the remaining width, height and new position after draw the legend
  const { remainingWidth, remainingHeight, topTranslate, leftTranslate } = drawLegend(
    svg,
    options,
    width,
    height,
    MARGIN
  );

  // Depend on the width of treemap, additional margin take account
  const additionalPlotMargin = Math.round(remainingWidth / ADDITIONAL_PLOT_MARGIN_DIVISION);
  // Double for left and right
  const totalAdditionalPlotMargin = additionalPlotMargin * 2;

  const seriesGroupWidth = remainingWidth - (MARGIN.left + MARGIN.right + totalAdditionalPlotMargin);
  const seriesGroupHeight = remainingHeight - (MARGIN.top + MARGIN.bottom);

  if (seriesGroupWidth <= 0 || seriesGroupHeight <= 0 || isEmpty(root)) {
    return;
  }

  // Add plot clip path to restrict the drawing area and for plot animation
  const { id: clipPathId, rect: clipPathRect } = addClipPath(svg, 0, seriesGroupHeight);
  const shouldDrawBanner = izMultiLevel && (izSliceLabel || izValueLabel);

  const seriesLeftTranslate = MARGIN.left + additionalPlotMargin + leftTranslate;
  const seriesTopTranslate = MARGIN.top + topTranslate;
  const seriesGroupTranslate = `translate(${seriesLeftTranslate}, ${seriesTopTranslate})`;

  // Draw the background
  drawBackground(
    svg,
    width,
    height,
    seriesGroupWidth + totalAdditionalPlotMargin,
    seriesGroupHeight,
    `translate(${seriesLeftTranslate - additionalPlotMargin}, ${seriesTopTranslate})`,
    options
  );

  // Graphic for plot area
  const seriesGroupGraphic = svg
    .append('g')
    .classed(LAYOUT_CLASSES.seriesGroup.class, true)
    .datum(LAYOUT_CLASSES.seriesGroup)
    .attr('transform', seriesGroupTranslate);

  // Graphic for the series, there is only one series in treemap
  const serieGraphic = seriesGroupGraphic
    .append('g')
    .classed('d3-series-0', true)
    .classed('d3-treemap-series', true)
    .attr('visibility', display ? 'visible' : 'hidden')
    .attr('clip-path', `url(#${clipPathId})`);

  // Graphic for labels
  const labelGraphic = svg
    .append('g')
    .classed(LAYOUT_CLASSES.labelGroup.class, true)
    .datum(LAYOUT_CLASSES.labelGroup)
    .attr('transform', seriesGroupTranslate)
    .attr('clip-path', `url(#${clipPathId})`)
    .append('g')
    .classed('d3-series-0', true);

  // Create tree map
  d3
    .treemap()
    .size([seriesGroupWidth, seriesGroupHeight])
    .paddingInner(TREEMAP_PADDING)
    .tile(d3.treemapSquarify)
    .paddingTop(d => (shouldDrawBanner && !d.data.isRoot ? TREEMAP_BANNER_HEIGHT : 0))
    .round(true)(root);

  // The data to bind
  const data = map(
    node => ({
      point: node.data,
      serie,
      node,
      label: {
        rotation: isLeafNode(node, treeHeight) ? rotation : 0,
        width: node.x1 - node.x0,
        height: node.y1 - node.y0,
        x: node.x0,
        y: node.y0
      }
    }),
    root.descendants()
  );

  // Draw treemap rects
  const rects = serieGraphic
    .selectAll('rect')
    .data(data)
    .enter()
    .append('rect')
    .style('cursor', cursor);

  // Add rect attributes
  rects
    .attr('x', ({ node }) => node.x0)
    .attr('y', ({ node }) => node.y0)
    .attr('width', ({ node }) => node.x1 - node.x0)
    .attr('height', ({ node }) => node.y1 - node.y0)
    .attr('fill', ({ point }) => point.color)
    .attr('fill-opacity', 1)
    .classed('d3-treemap-parent', ({ node, point }) => !isLeafNode(node, treeHeight) && !point.isRoot)
    .classed('d3-treemap-root', ({ point }) => point.isRoot)
    .classed('d3-treemap-leaf', ({ node }) => isLeafNode(node, treeHeight))
    .on('click.drilldown', d => !izMultiLevel && onDrilldown(d, serie, commonActions.onDrilldown));

  // Add a smaller height rect above the parent rect to fill black color only for the banner
  const parentRects = serieGraphic.selectAll('.d3-treemap-parent');
  if (shouldDrawBanner) {
    // These rects has smaller height equal to the banner height
    serieGraphic
      .selectAll('.d3-treemap-parent')
      .clone(true)
      .classed('d3-treemap-background', true)
      .classed('d3-treemap-parent', false)
      .attr('x', ({ node }) => node.x0)
      .attr('y', ({ node }) => node.y0 + TREEMAP_BANNER_HEIGHT - 1)
      .attr('width', ({ node }) => node.x1 - node.x0)
      .attr('height', ({ node }) => Math.max(0, node.y1 - node.y0 - TREEMAP_BANNER_HEIGHT + 1));

    parentRects.classed('d3-treemap-banner', true);
  } else {
    // Remove parent rects if we do not draw banner
    parentRects.remove();
  }

  // List of labels info
  const labelsInfo = filter(({ node }) => node.depth > 0, data);
  // All labels selections
  const labels = drawTreemapLabels(labelGraphic, labelsInfo, options, seriesGroupWidth, seriesGroupHeight, treeHeight);

  setLabelsClick(labelGraphic.selectAll('.u_textLink'), izendaSeriesConfig, get('commonActions.pointAction', options));

  const izHoverLabel = get('plotOptions.series.states.hover.enabled', options);

  if (display && izHoverLabel) {
    setHoverLabel(rects, rects, izMultiLevel, 'hoverSlice');
    setHoverLabel(labels, rects, izMultiLevel, 'hoverLabel');
  }

  // Tooltip
  addTooltipContent(
    this.baseOptions.tooltip,
    container,
    tooltipFormatter,
    rects,
    labels,
    MARGIN,
    topTranslate,
    leftTranslate + additionalPlotMargin
  );

  // Sort the graphic with z-index so graphic with higher z-index will be on top
  svg
    .selectAll(function() {
      return this.childNodes;
    })
    .filter(function() {
      return this.tagName === 'g';
    })
    .sort((a, b) => a['z-index'] - b['z-index']);

  // Animation
  beginPlotAnimation(clipPathRect, seriesGroupWidth);
}

function setHoverLabel(items: d3.Selection, rects: Array<SVGRectElement>, izMultiLevel: Boolean, eventGroup: String) {
  items
    .on(`mouseover.${eventGroup}`, d => onHoverLabel(d, rects, HOVER_OPACITY, izMultiLevel))
    .on(`mouseout.${eventGroup}`, d => onHoverLabel(d, rects, 1, izMultiLevel));
}

/**
 * Change slice opacity when hover on label
 * @param {Node} d
 * @param {Array} rectElements
 * @param {Number} opacity
 * @param {Boolean} isMultiLevel
 */
function onHoverLabel(
  { node: parentNode, point: parentPoint },
  rectElements: Array<SVGRectElement>,
  opacity: Number,
  isMultiLevel: Boolean
) {
  rectElements
    .filter(({ node, point }) =>
      isMultiLevel ? point === parentPoint || isParent(node, parentNode) : point.name === parentPoint.name
    )
    .transition()
    .duration(TRANSITION_DURATION)
    .attr('fill-opacity', opacity);

  /**
   * Check if parentNode is parent of node
   * @param {Node} node
   * @param {Node} parentNode
   * @returns {Boolean}
   */
  function isParent(node: Node, parentNode: Node) {
    let tempNode = node.parent;
    while (tempNode) {
      if (tempNode === parentNode) {
        return true;
      } else {
        tempNode = tempNode.parent;
      }
    }
    return false;
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/d3/treemap/Izenda.D3TreeMap.js